import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@astro/api'
import PageLayout from '../components/layout/PageLayout'
import FloatingFabs from '@astro/floating-fabs'
import MaharajProfileHeroSection from '../components/sections/maharajDetails/MaharajProfileHeroSection'
import MaharajAboutSection from '../components/sections/maharajDetails/MaharajAboutSection'
import MaharajPriorityTiersSection from '../components/sections/maharajDetails/MaharajPriorityTiersSection'
import MaharajDetailsSidebar from '../components/sections/maharajDetails/MaharajDetailsSidebar'
import GemstoneConsultationPromo from '../components/sections/booking/GemstoneConsultationPromo'
import AstrologerReviewsSection from '../components/sections/astrologerDetails/AstrologerReviewsSection'
import AstrologerFaqSection from '../components/sections/astrologerDetails/AstrologerFaqSection'
import SimilarAstrologersSection from '../components/sections/astrologerDetails/SimilarAstrologersSection'
import { decorateAstrologerDetail } from '../components/sections/astrologerDetails/astrologerDetailsData'

/** Search results may come back as a bare array or wrapped in a status envelope. */
function unwrapAstrologerList(response) {
  if (Array.isArray(response)) return response
  if (response && typeof response === 'object' && Array.isArray(response.data)) return response.data
  return []
}

export default function MaharajDetailsPage() {
  // This is a dedicated persona page, not a live astrologer-by-id route, so
  // the real backend record (needed for reviews + the "similar" exclusion)
  // is resolved by name — the same lookup MaharajBookingPage uses. The hero
  // and bio content below don't depend on it, so the page never blocks on
  // this resolving.
  const { data: searchData } = useQuery({
    queryKey: ['astrologer-search', 'raju maharaj'],
    queryFn: () => api.astrologers.filter({ search: 'Raju Maharaj', per_page: 5 }),
    staleTime: 5 * 60 * 1000,
  })

  const resolvedAstrologer = useMemo(() => {
    const list = unwrapAstrologerList(searchData)
    return list.find((a) => /maharaj/i.test(a.name || a.display_name || '')) ?? list[0] ?? null
  }, [searchData])

  const astro = resolvedAstrologer ? decorateAstrologerDetail(resolvedAstrologer) : null

  return (
    <PageLayout className="bg-background text-[#151b21] antialiased min-h-screen flex flex-col">
      <main className="flex-grow">
        <MaharajProfileHeroSection astro={astro} />
        <section className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-12 py-16">
          <div className="grid gap-8 lg:grid-cols-[1fr_360px] items-start">
            <div className="space-y-8">
              <MaharajAboutSection />
              <MaharajPriorityTiersSection />
              <GemstoneConsultationPromo />
              {astro && <AstrologerReviewsSection astro={astro} />}
              <AstrologerFaqSection />
            </div>
            <MaharajDetailsSidebar />
          </div>
        </section>
        <SimilarAstrologersSection excludeId={astro?.id} />
      </main>
      <FloatingFabs />
    </PageLayout>
  )
}
